/**
 * Checkout-page builder modal (P-1.11, P-1.12).
 *
 * Opens on double-click of a checkout_page node in the canvas. Two-pane
 * layout (FLOWS.md § 11.9 C4):
 *   - Left (~65%): live component preview using shared renderer
 *   - Right (~35%): section accordion + block library + per-block config
 *
 * Reorder is implemented with dnd-kit/sortable (already a dependency).
 * Visibility toggle per section. Required blocks show a lock icon.
 *
 * Custom HTML blocks get a dedicated editor that writes sanitized HTML
 * via src/lib/customHtmlStorage.ts.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { AlertTriangle, X, Plus, Monitor, Smartphone, Tablet, Palette, Sparkles, Undo2, Redo2, CreditCard } from "lucide-react";
import { clsx } from "clsx";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type {
  BlockId,
  BlockRules,
  CheckoutPageNode,
  Device,
  FlowDefinition,
  LayoutBlockConfig,
  LayoutBlocks,
  RuleClause,
  SectionName,
} from "../../types/flow";
import { useEditorStore } from "../designer/editorStore";
import { useBuilderStore } from "./builderStore";
import { normalizeLayoutBlocks } from "../../lib/normalizeLayout";
import { BRAND_SVG, CheckoutPagePreview, type WalletPreviewMode } from "../checkout-renderer/blocks";
import {
  saveCustomHtml,
  loadCustomHtml,
  deleteCustomHtml,
} from "../../lib/customHtmlStorage";
import { CHECKOUT_TEMPLATES } from "./templates";
import "./PageBuilder.css";

// ─── Block taxonomy (mirrors FLOWS.md § 11.9 C2) ─────────────────────────────

const SECTION_ORDER: SectionName[] = [
  "contact",
  "shipping",
  "offers",
  "payment",
  "legal",
  "support",
  "summary",
];

const SECTION_LABELS: Record<SectionName, string> = {
  contact: "Contact",
  shipping: "Shipping",
  offers: "Offers & upsells",
  payment: "Payment",
  legal: "Legal",
  support: "Support",
  summary: "Order summary",
};

/**
 * Template id shown in the block palette. Not a real BlockId — each time
 * the merchant picks it, we mint a fresh instance id (custom_html_<rand>)
 * and push *that* into section.blocks so multiple Custom HTML blocks can
 * coexist in the same section. Rendering detects `custom_html_*` by prefix.
 */
const CUSTOM_HTML_TEMPLATE_ID = "custom_html" as const;

function isCustomHtmlId(id: string): boolean {
  return id.startsWith("custom_html_");
}

function mintCustomHtmlId(): `custom_html_${string}` {
  const bytes = crypto.getRandomValues(new Uint8Array(4));
  const hex = Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return `custom_html_${hex}`;
}

const REQUIRED_BLOCKS_BY_SECTION: Record<SectionName, BlockId[]> = {
  contact: ["email"],
  shipping: ["address", "shipping_method"],
  offers: [],
  payment: ["payment_method_selector", "pay_button"],
  legal: [],
  support: [],
  summary: ["line_items", "totals"],
};

/**
 * Human-readable label for a block id. Custom HTML instances all collapse
 * to "Custom HTML" so the merchant sees a clean list, not random hex ids.
 */
function blockLabel(id: string): string {
  if (id === CUSTOM_HTML_TEMPLATE_ID || id.startsWith("custom_html_")) {
    return "Custom HTML";
  }
  return BLOCK_LABELS[id as StaticBlockId] ?? id;
}

/** Palette entry shown in a section's "Add block" list. */
type PaletteId = BlockId | typeof CUSTOM_HTML_TEMPLATE_ID;

const AVAILABLE_BLOCKS: Record<SectionName, PaletteId[]> = {
  contact: [
    "email",
    "marketing_consent",
    "newsletter_opt_in",
    "sms_opt_in",
    "phone",
    "login_link",
    "loyalty_login_card",
    "save_my_info",
    "trust_badges",
    "text_banner",
    "image_banner",
    "divider",
    CUSTOM_HTML_TEMPLATE_ID,
  ],
  shipping: [
    // Address absorbs company / VAT / PO / billing-same-as-shipping as
    // toggleable fields — merchants configure those inside the Address
    // block's settings drawer, not as separate blocks.
    "address",
    "shipping_method",
    "order_notes",
    "gift_message",
    "shipping_insurance",
    "delivery_date",
    "pickup_point",
    "trust_badges",
    "text_banner",
    "image_banner",
    "divider",
    CUSTOM_HTML_TEMPLATE_ID,
  ],
  offers: [
    "upsell_slot",
    "recommendation_cards",
    "incentive_banner",
    "testimonial_cards",
    "trust_badges",
    "text_banner",
    "image_banner",
    "divider",
    CUSTOM_HTML_TEMPLATE_ID,
  ],
  payment: [
    // billing-same-as-shipping is a toggle on the Address form now, not
    // a block here. It's more natural next to the shipping address input.
    "payment_method_selector",
    "save_card_toggle",
    "terms_checkbox",
    "age_verification",
    "subscription_consent",
    "pay_button",
    "trust_badges",
    "text_banner",
    "image_banner",
    "divider",
    CUSTOM_HTML_TEMPLATE_ID,
  ],
  legal: [
    "legal_links",
    "cookie_notice",
    "trust_badges",
    "text_banner",
    "image_banner",
    "divider",
    CUSTOM_HTML_TEMPLATE_ID,
  ],
  support: [
    "support_block",
    "help_center_link",
    "benefit_rows",
    "testimonial_cards",
    "trust_badges",
    "text_banner",
    "image_banner",
    "divider",
    CUSTOM_HTML_TEMPLATE_ID,
  ],
  summary: [
    "loyalty_login_card",
    "recommendation_cards",
    "benefit_rows",
    "testimonial_cards",
    "product_thumbnails",
    "line_items",
    "totals",
    "tax_breakdown",
    "ic_fee_transparency",
    "gift_card_input",
    "promo_code",
    "trust_badges",
    "divider",
    "text_banner",
    "image_banner",
    CUSTOM_HTML_TEMPLATE_ID,
  ],
};

const REQUIRED_SECTIONS: SectionName[] = ["contact", "shipping", "payment", "summary"];

type StaticBlockId = Exclude<BlockId, `custom_html_${string}`>;

const WALLET_PREVIEW_OPTIONS: Array<{ id: WalletPreviewMode; label: string }> = [
  { id: "design", label: "Design" },
  { id: "ios_safari", label: "iOS Safari" },
  { id: "mac_safari", label: "Mac Safari" },
  { id: "chrome_desktop", label: "Chrome desktop" },
  { id: "android_chrome", label: "Android Chrome" },
  { id: "unavailable", label: "Wallets unavailable" },
];

const BLOCK_LABELS: Record<StaticBlockId, string> = {
  email: "Email input",
  marketing_consent: "Marketing consent",
  newsletter_opt_in: "Newsletter opt-in (GDPR)",
  sms_opt_in: "SMS opt-in (TCPA)",
  login_link: "Sign-in link",
  loyalty_login_card: "Loyalty / login prompt",
  save_my_info: "Save my info for next time",
  phone: "Phone input",
  company_field: "Company field",
  address: "Address form",
  shipping_method: "Shipping method selector",
  order_notes: "Order notes",
  gift_message: "Gift message",
  shipping_insurance: "Shipping insurance",
  vat_id_field: "VAT ID (EU)",
  po_number: "PO number",
  delivery_date: "Delivery date picker",
  pickup_point: "Pickup point",
  payment_method_selector: "Payment method selector",
  billing_same_as_shipping: "Billing = Shipping toggle",
  save_card_toggle: "Save card for next time",
  terms_checkbox: "Terms & conditions checkbox",
  age_verification: "Age verification",
  subscription_consent: "Subscription consent",
  pay_button: "Pay button",
  line_items: "Line items",
  totals: "Totals",
  promo_code: "Promo code input",
  trust_badges: "Trust badges",
  divider: "Divider",
  text_banner: "Text banner",
  image_banner: "Image banner",
  tax_breakdown: "Tax breakdown (expandable)",
  gift_card_input: "Gift card / store credit",
  ic_fee_transparency: "IC++ transparency",
  product_thumbnails: "Product thumbnails",
  upsell_slot: "Upsell / recommendations",
  recommendation_cards: "Recommendation cards",
  incentive_banner: "Incentive banner",
  benefit_rows: "Benefit rows",
  testimonial_cards: "Reviews / testimonials",
  legal_links: "Legal links (Terms / Privacy / Refund / Cookies)",
  cookie_notice: "Cookie notice",
  support_block: "Support block",
  help_center_link: "Help center link",
};

function defaultLayoutForNode(): LayoutBlocks {
  return {
    schema_version: 1,
    sections: {
      contact: { visible: true, blocks: ["email"] },
      shipping: { visible: true, blocks: ["address", "shipping_method"] },
      offers: { visible: false, blocks: [] },
      payment: { visible: true, blocks: ["payment_method_selector", "pay_button"] },
      legal: { visible: false, blocks: [] },
      support: { visible: false, blocks: [] },
      summary: { visible: true, blocks: ["line_items", "totals"] },
    },
  };
}

function walletMethodsRequiredByLayout(_layout: LayoutBlocks): string[] {
  // Express buttons were removed (2026-04-24); no wallet method is
  // "required" by the layout any more. Kept as a no-op for callers that
  // still want a list so the surrounding ensure-payment-methods logic
  // compiles cleanly.
  return [];
}

function missingWalletMethods(layout: LayoutBlocks, methods: string[]): string[] {
  const enabled = new Set(methods.map((m) => m.replace(/-/g, "_")));
  return walletMethodsRequiredByLayout(layout).filter((method) => !enabled.has(method));
}

// ─── Main component ──────────────────────────────────────────────────────────

export function PageBuilder() {
  const open = useBuilderStore((s) => s.open);
  const nodeId = useBuilderStore((s) => s.nodeId);
  const close = useBuilderStore((s) => s.close);
  const definition = useEditorStore((s) => s.definition);
  const updateNode = useEditorStore((s) => s.updateNode);

  const [viewport, setViewport] = useState<Device>("desktop");
  const [walletPreviewMode, setWalletPreviewMode] = useState<WalletPreviewMode>("design");
  const [draftLayout, setDraftLayoutRaw] = useState<LayoutBlocks | null>(null);
  const [selectedBlock, setSelectedBlock] = useState<{ section: SectionName; block: BlockId } | null>(null);
  const [themeOpen, setThemeOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);

  // Undo/redo history (Wave 7). undoStack[top] === current draftLayout.
  const undoStackRef = useRef<LayoutBlocks[]>([]);
  const redoStackRef = useRef<LayoutBlocks[]>([]);
  const [, forceHistoryRender] = useState(0);
  const bumpHistory = useCallback(() => forceHistoryRender((n) => n + 1), []);

  const setDraftLayout = useCallback(
    (next: LayoutBlocks | ((prev: LayoutBlocks | null) => LayoutBlocks)) => {
      setDraftLayoutRaw((prev) => {
        const resolved =
          typeof next === "function" ? (next as (p: LayoutBlocks | null) => LayoutBlocks)(prev) : next;
        if (prev) {
          undoStackRef.current.push(prev);
          if (undoStackRef.current.length > 50) undoStackRef.current.shift();
          redoStackRef.current = [];
          bumpHistory();
        }
        return resolved;
      });
    },
    [bumpHistory],
  );

  const undo = useCallback(() => {
    if (undoStackRef.current.length === 0) return;
    const prev = undoStackRef.current.pop()!;
    setDraftLayoutRaw((cur) => {
      if (cur) redoStackRef.current.push(cur);
      return prev;
    });
    bumpHistory();
  }, [bumpHistory]);

  const redo = useCallback(() => {
    if (redoStackRef.current.length === 0) return;
    const next = redoStackRef.current.pop()!;
    setDraftLayoutRaw((cur) => {
      if (cur) undoStackRef.current.push(cur);
      return next;
    });
    bumpHistory();
  }, [bumpHistory]);

  const canUndo = undoStackRef.current.length > 0;
  const canRedo = redoStackRef.current.length > 0;

  useEffect(() => {
    if (!open || !nodeId || !definition) return;
    const node = definition.nodes[nodeId];
    if (node?.type !== "checkout_page") return;
    // Strip unknown sections / blocks from stale data so the next save
    // writes clean JSON. Essential for flows created before removed block
    // types like `express_checkout` — the publish validator rejects them
    // on the server, but the Studio pane would still render their panels
    // if we didn't filter here.
    const loaded = node.layout_blocks ? normalizeLayoutBlocks(node.layout_blocks) : defaultLayoutForNode();
    setDraftLayoutRaw(loaded);
    setSelectedBlock(null);
    undoStackRef.current = [];
    redoStackRef.current = [];
    bumpHistory();
  }, [open, nodeId, definition, bumpHistory]);

  // Global keyboard shortcuts for undo/redo (only while the modal is open).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      if (!meta) return;
      const k = e.key.toLowerCase();
      if (k === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((k === "z" && e.shiftKey) || k === "y") {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, undo, redo]);

  if (!open || !nodeId || !definition || !draftLayout) return null;
  const node = definition.nodes[nodeId];
  if (node?.type !== "checkout_page") return null;

  const noMethods = (node.payment_methods ?? []).length === 0;
  const walletMethodMismatches = missingWalletMethods(draftLayout, node.payment_methods ?? []);

  function save() {
    if (!nodeId || !draftLayout) return;
    updateNode(nodeId, { layout_blocks: draftLayout });
    close();
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-stretch" onClick={close}>
      <div
        className="pb-shell pb-modal-root m-6 rounded-lg shadow-xl border border-neutral-200 dark:border-neutral-800 flex-1 flex flex-col overflow-hidden relative"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-4 py-2.5 border-b border-neutral-200 dark:border-neutral-800 flex items-center">
          <div>
            <h2 className="text-sm font-semibold">Checkout page builder</h2>
            <p className="text-xs text-neutral-500 font-mono">{nodeId}</p>
          </div>
          <div className="flex-1" />
          <div className="flex rounded-md border border-neutral-200 dark:border-neutral-700 overflow-hidden mr-2">
            <button
              onClick={undo}
              disabled={!canUndo}
              title="Undo (⌘Z)"
              aria-label="Undo"
              className={clsx(
                "px-2 py-1 text-xs flex items-center",
                canUndo
                  ? "text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-900"
                  : "text-neutral-300 dark:text-neutral-700 cursor-not-allowed",
              )}
            >
              <Undo2 className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={redo}
              disabled={!canRedo}
              title="Redo (⌘⇧Z)"
              aria-label="Redo"
              className={clsx(
                "px-2 py-1 text-xs flex items-center",
                canRedo
                  ? "text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-900"
                  : "text-neutral-300 dark:text-neutral-700 cursor-not-allowed",
              )}
            >
              <Redo2 className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="flex rounded-md border border-neutral-200 dark:border-neutral-700 overflow-hidden">
            {(
              [
                { id: "desktop", label: "Desktop", Icon: Monitor },
                { id: "tablet", label: "Tablet", Icon: Tablet },
                { id: "mobile", label: "Mobile", Icon: Smartphone },
              ] as const
            ).map(({ id, label, Icon }) => (
              <button
                key={id}
                onClick={() => setViewport(id)}
                className={clsx(
                  "px-2 py-1 text-xs flex items-center gap-1",
                  viewport === id
                    ? "bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900"
                    : "text-neutral-500 hover:bg-neutral-50 dark:hover:bg-neutral-900",
                )}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>
          <label className="ml-2 flex items-center gap-1.5 rounded-md border border-neutral-200 dark:border-neutral-700 px-2 py-1 text-xs text-neutral-600 dark:text-neutral-300">
            <CreditCard className="w-3.5 h-3.5" />
            <select
              value={walletPreviewMode}
              onChange={(e) => setWalletPreviewMode(e.target.value as WalletPreviewMode)}
              aria-label="Wallet preview mode"
              className="bg-transparent text-xs outline-none"
            >
              {WALLET_PREVIEW_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <button
            onClick={() => setTemplatesOpen((v) => !v)}
            className="ml-3 rounded-md border px-3 py-1.5 text-sm font-medium flex items-center gap-1 border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-900"
            aria-pressed={templatesOpen}
          >
            <Sparkles className="w-3.5 h-3.5" />
            Templates
          </button>
          <button
            onClick={() => {
              setThemeOpen((v) => !v);
              setSelectedBlock(null);
            }}
            className={clsx(
              "ml-2 rounded-md border px-3 py-1.5 text-sm font-medium flex items-center gap-1",
              themeOpen
                ? "bg-category-terminal/10 border-category-terminal/30 text-category-terminal"
                : "border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-900",
            )}
            aria-pressed={themeOpen}
          >
            <Palette className="w-3.5 h-3.5" />
            Theme
          </button>
          <button
            onClick={save}
            className="ml-2 rounded-md bg-category-terminal/10 border border-category-terminal/30 text-category-terminal px-3 py-1.5 text-sm font-medium hover:bg-category-terminal/20"
          >
            Save
          </button>
          <button
            onClick={close}
            className="ml-2 text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        {noMethods ? (
          <div
            className="px-4 py-2 border-b border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-200 flex items-center gap-2 text-xs"
            role="alert"
          >
            <AlertTriangle className="w-4 h-4 flex-none" />
            <span>
              No payment methods are enabled on this checkout page. Buyers won't be able to pay.
              Open the checkout page node in the canvas and pick at least one payment method before publishing.
            </span>
          </div>
        ) : null}

        {walletMethodMismatches.length > 0 ? (
          <div
            className="px-4 py-2 border-b border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-200 flex items-center gap-2 text-xs"
            role="alert"
          >
            <AlertTriangle className="w-4 h-4 flex-none" />
            <span className="flex-1">
              Express wallet blocks are on the page, but {walletMethodMismatches.join(", ")}
              {" "}is not enabled in payment methods. Buyers will not see those buttons.
            </span>
            <button
              type="button"
              onClick={() => {
                updateNode(nodeId, {
                  payment_methods: [
                    ...(node.payment_methods ?? []),
                    ...walletMethodMismatches,
                  ],
                });
              }}
              className="rounded border border-amber-300 dark:border-amber-700 px-2 py-1 font-medium hover:bg-amber-100 dark:hover:bg-amber-900/50"
            >
              Enable wallets
            </button>
          </div>
        ) : null}

        {templatesOpen ? (
          <TemplatesOverlay
            onClose={() => setTemplatesOpen(false)}
            onApply={(tpl) => {
              // Apply atomically: layout replaces draft; theme + suggested
              // methods land through updateNode so they persist alongside
              // any merchant-edited fields.
              setDraftLayout(normalizeLayoutBlocks(tpl.layout_blocks));
              const patch: Partial<CheckoutPageNode> = {};
              if (tpl.theme_overrides) patch.theme_overrides = tpl.theme_overrides;
              if (tpl.suggested_payment_methods) {
                patch.payment_methods = tpl.suggested_payment_methods;
              }
              if (Object.keys(patch).length > 0) updateNode(nodeId, patch);
              setTemplatesOpen(false);
              setSelectedBlock(null);
            }}
          />
        ) : null}

        <div
          className="flex-1 min-h-0 grid divide-x divide-neutral-200 dark:divide-neutral-800"
          style={{
            gridTemplateColumns:
              !themeOpen && selectedBlock ? "1fr 340px 360px" : "1fr 360px",
          }}
        >
          <div
            className="pb-preview-pane overflow-y-auto p-6"
            onClick={() => setSelectedBlock(null)}
          >
            <div
              className={
                viewport === "mobile"
                  ? "max-w-sm mx-auto"
                  : viewport === "tablet"
                    ? "max-w-2xl mx-auto"
                    : ""
              }
            >
              <CheckoutPagePreview
                node={{ ...node, layout_blocks: draftLayout }}
                viewport={viewport}
                walletPreviewMode={walletPreviewMode}
                selection={selectedBlock ? { block: selectedBlock.block } : null}
                onSelectBlock={(b) => {
                  const foundSection = (SECTION_ORDER as SectionName[]).find((s) =>
                    draftLayout.sections[s]?.blocks.includes(b),
                  );
                  if (foundSection) setSelectedBlock({ section: foundSection, block: b });
                }}
                shopifyShopDomain={triggerShopifyShopDomain(definition)}
              />
            </div>
          </div>
          {!themeOpen && selectedBlock ? (
            <div
              className="pb-popout overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <BlockConfigPanel
                block={selectedBlock.block}
                layout={draftLayout}
                onLayoutChange={setDraftLayout}
                onClose={() => setSelectedBlock(null)}
              />
            </div>
          ) : null}
          <div className="pb-right-pane overflow-y-auto">
            {themeOpen ? (
              <ThemePanel
                node={node}
                onChange={(overrides) => updateNode(nodeId, { theme_overrides: overrides })}
                onClose={() => setThemeOpen(false)}
              />
            ) : (
              <SectionEditor
                layout={draftLayout}
                onChange={setDraftLayout}
                selectedBlock={selectedBlock}
                onSelectBlock={setSelectedBlock}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Section editor (accordion + dnd) ────────────────────────────────────────

function SectionEditor({
  layout,
  onChange,
  selectedBlock,
  onSelectBlock,
}: {
  layout: LayoutBlocks;
  onChange: (l: LayoutBlocks) => void;
  selectedBlock: { section: SectionName; block: BlockId } | null;
  onSelectBlock: (v: { section: SectionName; block: BlockId } | null) => void;
}) {
  const effectiveOrder = resolveSectionOrder(layout.section_order);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  function onSectionDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = effectiveOrder.indexOf(active.id as SectionName);
    const newIndex = effectiveOrder.indexOf(over.id as SectionName);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(effectiveOrder, oldIndex, newIndex);
    onChange({ ...layout, section_order: next });
  }

  return (
    <div className="p-3 space-y-3">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onSectionDragEnd}>
        <SortableContext items={effectiveOrder} strategy={verticalListSortingStrategy}>
          {effectiveOrder.map((section) => {
        const cfg = layout.sections[section];
        if (!cfg) return null;
        const extraControls = null;
        return (
          <SectionPanel
            key={section}
            section={section}
            cfg={cfg}
            blockRules={layout.block_rules}
            extraControls={extraControls}
            selectedBlock={selectedBlock}
            onSelectBlock={onSelectBlock}
            onToggleVisible={(visible) => {
              onChange({
                ...layout,
                sections: { ...layout.sections, [section]: { ...cfg, visible } },
              });
            }}
            onReorder={(blocks) =>
              onChange({
                ...layout,
                sections: { ...layout.sections, [section]: { ...cfg, blocks } },
              })
            }
            onRemove={(block) => {
              if (REQUIRED_BLOCKS_BY_SECTION[section].includes(block)) {
                alert(`"${blockLabel(block)}" is required in this section.`);
                return;
              }
              onChange({
                ...layout,
                sections: {
                  ...layout.sections,
                  [section]: { ...cfg, blocks: cfg.blocks.filter((b) => b !== block) },
                },
              });
              if (selectedBlock?.section === section && selectedBlock.block === block) {
                onSelectBlock(null);
              }
            }}
            onAdd={(block) =>
              onChange({
                ...layout,
                sections: { ...layout.sections, [section]: { ...cfg, blocks: [...cfg.blocks, block] } },
              })
            }
          />
        );
      })}
        </SortableContext>
      </DndContext>
    </div>
  );
}

function resolveSectionOrder(chosen: SectionName[] | undefined): SectionName[] {
  if (!chosen || chosen.length === 0) return SECTION_ORDER;
  // Build the final order: start from chosen, then append any sections the
  // merchant hasn't placed (in their canonical position relative to each other).
  const seen = new Set<SectionName>();
  const out: SectionName[] = [];
  for (const s of chosen) {
    if (SECTION_ORDER.includes(s) && !seen.has(s)) {
      out.push(s);
      seen.add(s);
    }
  }
  for (const s of SECTION_ORDER) {
    if (!seen.has(s)) out.push(s);
  }
  return out;
}

function SectionPanel({
  section,
  cfg,
  blockRules,
  extraControls,
  selectedBlock,
  onSelectBlock,
  onToggleVisible,
  onReorder,
  onRemove,
  onAdd,
}: {
  section: SectionName;
  cfg: LayoutBlocks["sections"][SectionName];
  blockRules?: LayoutBlocks["block_rules"];
  extraControls?: ReactNode;
  selectedBlock: { section: SectionName; block: BlockId } | null;
  onSelectBlock: (v: { section: SectionName; block: BlockId } | null) => void;
  onToggleVisible: (visible: boolean) => void;
  onReorder: (blocks: BlockId[]) => void;
  onRemove: (block: BlockId) => void;
  onAdd: (block: BlockId) => void;
}) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const isRequired = REQUIRED_SECTIONS.includes(section);
  const {
    attributes: sectionAttributes,
    listeners: sectionListeners,
    setNodeRef: setSectionNodeRef,
    transform: sectionTransform,
    transition: sectionTransition,
    isDragging: isSectionDragging,
  } = useSortable({ id: section });
  const sectionStyle: CSSProperties = {
    transform: CSS.Transform.toString(sectionTransform),
    transition: sectionTransition,
    opacity: isSectionDragging ? 0.5 : 1,
  };

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = cfg.blocks.indexOf(active.id as BlockId);
    const newIndex = cfg.blocks.indexOf(over.id as BlockId);
    if (oldIndex >= 0 && newIndex >= 0) {
      onReorder(arrayMove(cfg.blocks, oldIndex, newIndex));
    }
  }

  // Custom-HTML template ALWAYS stays in the palette (you can add more of
  // them). For other blocks, hide from the palette once they're in the list.
  const availableToAdd = AVAILABLE_BLOCKS[section].filter(
    (b) => b === CUSTOM_HTML_TEMPLATE_ID || !cfg.blocks.includes(b as BlockId),
  );

  return (
    <div
      ref={setSectionNodeRef}
      style={sectionStyle}
      className="pb-section"
    >
      <div className="pb-section-header">
        <span
          {...sectionAttributes}
          {...sectionListeners}
          className="pb-drag-handle select-none text-sm"
          title="Drag to reorder section"
        >
          ⋮⋮
        </span>
        <span className="pb-section-title">{SECTION_LABELS[section]}</span>
        {isRequired ? (
          <span className="rounded-full bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 text-[10px] font-medium px-2 py-0.5">
            Required
          </span>
        ) : null}
        <div className="flex-1" />
        {isRequired ? null : (
          <label className="text-xs flex items-center gap-1">
            <input
              type="checkbox"
              checked={cfg.visible}
              onChange={(e) => onToggleVisible(e.target.checked)}
            />
            Visible
          </label>
        )}
      </div>
      {cfg.visible ? (
        <>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={cfg.blocks} strategy={verticalListSortingStrategy}>
              <ul className="p-2 space-y-1">
                {cfg.blocks.map((block) => (
                  <BlockRow
                    key={block}
                    id={block}
                    selected={
                      selectedBlock?.section === section && selectedBlock.block === block
                    }
                    required={REQUIRED_BLOCKS_BY_SECTION[section].includes(block)}
                    hiddenOn={blockRules?.[block]?.hide_on_device ?? []}
                    onClick={() => onSelectBlock({ section, block })}
                    onRemove={() => onRemove(block)}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
          {availableToAdd.length > 0 ? (
            <details className="px-2 pb-2">
              <summary className="cursor-pointer text-xs text-neutral-500 px-1 py-1 hover:text-neutral-900 dark:hover:text-neutral-100">
                <Plus className="inline w-3 h-3 mr-1" />
                Add block
              </summary>
              <div className="mt-1 space-y-0.5">
                {availableToAdd.map((b) => (
                  <button
                    key={b}
                    onClick={() =>
                      onAdd(b === CUSTOM_HTML_TEMPLATE_ID ? mintCustomHtmlId() : (b as BlockId))
                    }
                    className="block w-full text-left text-xs rounded px-1.5 py-1 hover:bg-neutral-100 dark:hover:bg-neutral-900"
                  >
                    {blockLabel(b)}
                  </button>
                ))}
              </div>
            </details>
          ) : null}
          {extraControls}
        </>
      ) : null}
    </div>
  );
}

function BlockRow({
  id,
  selected,
  required,
  hiddenOn,
  onClick,
  onRemove,
}: {
  id: BlockId;
  selected: boolean;
  required: boolean;
  hiddenOn: Device[];
  onClick: () => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  return (
    <li
      ref={setNodeRef}
      style={style}
      className={clsx(
        "pb-block-row",
        selected && "pb-block-row-selected",
      )}
    >
      <span
        {...attributes}
        {...listeners}
        className="pb-drag-handle select-none"
      >
        ⋮⋮
      </span>
      <button onClick={onClick} className="flex-1 text-left truncate">
        {blockLabel(id)}
      </button>
      {hiddenOn.length > 0 ? (
        <span
          className="rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-[10px] font-medium px-1.5 py-0.5"
          title={`Hidden on ${hiddenOn.join(", ")}`}
        >
          hide {hiddenOn.map((d) => d[0]).join("")}
        </span>
      ) : null}
      {required ? (
        <span className="rounded-full bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 text-[10px] font-medium px-1.5 py-0.5">
          Required
        </span>
      ) : (
        <button onClick={onRemove} aria-label="Remove block" className="text-neutral-500 hover:text-category-error">
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </li>
  );
}

// ─── Per-block config panel ──────────────────────────────────────────────────

/**
 * Blocks without any per-block Content config. For these the popout shows
 * only Visibility + Conditions — we don't render the "no configuration in v1"
 * placeholder (it's noise).
 */
const BLOCKS_WITHOUT_CONTENT_CONFIG: ReadonlySet<BlockId> = new Set([
  "email",
  // "address" moved OUT — now carries company/VAT/PO/billing toggles.
  "shipping_method",
  "order_notes",
  "pay_button",
  "line_items",
  "totals",
  "divider",
]);

function BlockConfigPanel({
  block,
  layout,
  onLayoutChange,
  onClose,
}: {
  block: BlockId;
  layout: LayoutBlocks;
  onLayoutChange: (l: LayoutBlocks) => void;
  onClose?: () => void;
}) {
  const config = layout.block_config ?? {};
  const rules: BlockRules = layout.block_rules?.[block] ?? {};
  const hasContent = !BLOCKS_WITHOUT_CONTENT_CONFIG.has(block);
  const onConfigChange = (cfg: LayoutBlockConfig) =>
    onLayoutChange({ ...layout, block_config: cfg });
  const onRulesChange = (next: BlockRules) => {
    const current = layout.block_rules ?? {};
    const cleaned: BlockRules = {};
    if (next.hide_on_device && next.hide_on_device.length > 0) {
      cleaned.hide_on_device = next.hide_on_device;
    }
    if (next.visibility_rules) {
      const g = next.visibility_rules;
      const hasAll = g.all && g.all.length > 0;
      const hasAny = g.any && g.any.length > 0;
      if (hasAll || hasAny) cleaned.visibility_rules = g;
    }
    const merged: Partial<Record<BlockId, BlockRules>> = { ...current };
    if (Object.keys(cleaned).length === 0) {
      delete merged[block];
    } else {
      merged[block] = cleaned;
    }
    onLayoutChange({ ...layout, block_rules: merged });
  };

  return (
    <div className="pb-config-panel">
      <div className="flex items-center gap-2">
        <h4>{blockLabel(block)}</h4>
        <div className="flex-1" />
        {onClose ? (
          <button
            onClick={onClose}
            aria-label="Close block settings"
            className="text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        ) : null}
      </div>
      {hasContent ? (
        <div className="pb-config-group">
          <p className="pb-config-group-title">Content</p>
          <div className="space-y-2 text-xs">
            <BlockConfigFields block={block} config={config} onChange={onConfigChange} />
          </div>
        </div>
      ) : null}
      <div className="pb-config-group">
        <p className="pb-config-group-title">Visibility</p>
        <VisibilityRow rules={rules} onChange={onRulesChange} />
      </div>
      <div className="pb-config-group">
        <p className="pb-config-group-title">Conditions</p>
        <ConditionalRulesRow rules={rules} onChange={onRulesChange} />
      </div>
    </div>
  );
}

// ─── Templates overlay (Wave 6) ─────────────────────────────────────────────

function TemplatesOverlay({
  onClose,
  onApply,
}: {
  onClose: () => void;
  onApply: (tpl: (typeof CHECKOUT_TEMPLATES)[number]) => void;
}) {
  return (
    <div
      className="absolute inset-0 z-10 bg-black/30 flex items-center justify-center p-6"
      onClick={onClose}
    >
      <div
        className="rounded-lg bg-white dark:bg-neutral-950 shadow-2xl border border-neutral-200 dark:border-neutral-800 max-w-3xl w-full max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-800 flex items-center">
          <div>
            <h3 className="text-sm font-semibold">Start from a template</h3>
            <p className="text-xs text-neutral-500">
              Applying a template replaces the current layout, theme, and suggested payment methods.
              Your flow wiring is untouched.
            </p>
          </div>
          <div className="flex-1" />
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100"
          >
            <X className="w-5 h-5" />
          </button>
        </header>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4">
          {CHECKOUT_TEMPLATES.map((tpl) => (
            <button
              key={tpl.id}
              onClick={() => onApply(tpl)}
              className="text-left rounded-md border border-neutral-200 dark:border-neutral-800 p-3 hover:border-category-terminal/40 hover:shadow-sm transition-shadow bg-white dark:bg-neutral-900/40 group"
            >
              <div className="flex items-center gap-2">
                <span
                  className="inline-block w-4 h-4 rounded-full border border-neutral-200 dark:border-neutral-700"
                  style={{ background: tpl.theme_overrides?.brand_color ?? "#999" }}
                />
                <span className="text-sm font-medium group-hover:text-category-terminal">
                  {tpl.name}
                </span>
              </div>
              <p className="text-xs text-neutral-500 mt-1.5">{tpl.description}</p>
              {tpl.suggested_payment_methods ? (
                <p className="text-[11px] text-neutral-400 mt-2 font-mono">
                  Methods: {tpl.suggested_payment_methods.join(", ")}
                </p>
              ) : null}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Theme panel (Wave 2d) ──────────────────────────────────────────────────

type ThemeOverrides = NonNullable<CheckoutPageNode["theme_overrides"]>;

function ThemePanel({
  node,
  onChange,
  onClose,
}: {
  node: CheckoutPageNode;
  onChange: (overrides: ThemeOverrides | undefined) => void;
  onClose: () => void;
}) {
  const overrides: ThemeOverrides = node.theme_overrides ?? {};

  const patch = (part: Partial<ThemeOverrides>) => {
    const next: ThemeOverrides = { ...overrides, ...part };
    // Drop keys with empty-string / undefined values so resets fall back
    // to the resolved theme.
    const cleaned: ThemeOverrides = {};
    for (const [k, v] of Object.entries(next)) {
      if (v === undefined || v === "") continue;
      (cleaned as Record<string, unknown>)[k] = v;
    }
    onChange(Object.keys(cleaned).length === 0 ? undefined : cleaned);
  };

  const color = (
    label: string,
    key: keyof ThemeOverrides,
    fallback: string,
  ) => (
    <div className="flex items-center gap-2">
      <label className="flex-1 text-xs text-neutral-700 dark:text-neutral-300">{label}</label>
      <input
        type="color"
        value={(overrides[key] as string) ?? fallback}
        onChange={(e) => patch({ [key]: e.target.value } as Partial<ThemeOverrides>)}
        className="w-8 h-6 rounded border border-neutral-200 dark:border-neutral-700 bg-transparent"
      />
      <input
        type="text"
        value={(overrides[key] as string) ?? ""}
        onChange={(e) => patch({ [key]: e.target.value || undefined } as Partial<ThemeOverrides>)}
        placeholder={fallback}
        className="w-20 rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-1.5 py-0.5 text-xs font-mono"
      />
    </div>
  );

  return (
    <div className="p-3 space-y-3">
      <div className="flex items-center gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Theme</p>
        <div className="flex-1" />
        <button
          onClick={onClose}
          aria-label="Close theme panel"
          className="text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <p className="text-[11px] text-neutral-500">
        Overrides apply to this checkout page only. Leave a field empty to fall back to the
        theme default ({node.theme_id}).
      </p>

      <section className="space-y-1.5 border-t border-neutral-200 dark:border-neutral-800 pt-2">
        <p className="text-[11px] font-medium text-neutral-600 dark:text-neutral-400 uppercase tracking-wide">
          Brand
        </p>
        <LogoUploader
          current={overrides.logo_url}
          onChange={(url) => patch({ logo_url: url })}
        />
        {color("Brand color", "brand_color", "#1773b0")}
        {color("Pay button", "pay_button_color", "#ef4123")}
        {color("Pay button text", "pay_button_text_color", "#ffffff")}
      </section>

      <section className="space-y-1.5 border-t border-neutral-200 dark:border-neutral-800 pt-2">
        <p className="text-[11px] font-medium text-neutral-600 dark:text-neutral-400 uppercase tracking-wide">
          Surface
        </p>
        {color("Page background", "bg_color", "#ffffff")}
        {color("Summary background", "summary_bg_color", "#f4f4f5")}
        {color("Border", "border_color", "#e4e4e7")}
      </section>

      <section className="space-y-1.5 border-t border-neutral-200 dark:border-neutral-800 pt-2">
        <p className="text-[11px] font-medium text-neutral-600 dark:text-neutral-400 uppercase tracking-wide">
          Text
        </p>
        {color("Primary text", "text_color", "#18181b")}
        {color("Muted text", "text_muted_color", "#71717a")}
      </section>

      <section className="space-y-2 border-t border-neutral-200 dark:border-neutral-800 pt-2">
        <p className="text-[11px] font-medium text-neutral-600 dark:text-neutral-400 uppercase tracking-wide">
          Shape & type
        </p>
        <div className="flex items-center gap-2">
          <label className="flex-1 text-xs text-neutral-700 dark:text-neutral-300">Corner radius</label>
          <select
            value={overrides.radius_px ?? 6}
            onChange={(e) => patch({ radius_px: Number.parseInt(e.target.value, 10) })}
            className="rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-1.5 py-0.5 text-xs"
          >
            <option value={0}>Sharp</option>
            <option value={4}>Small</option>
            <option value={6}>Medium (default)</option>
            <option value={10}>Large</option>
            <option value={16}>Pill</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-neutral-700 dark:text-neutral-300 block mb-1">
            Font family
          </label>
          <input
            type="text"
            value={overrides.font_family ?? ""}
            onChange={(e) => patch({ font_family: e.target.value || undefined })}
            placeholder='"Inter", system-ui, sans-serif'
            className="w-full rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-1.5 py-1 text-xs font-mono"
          />
        </div>
      </section>

      <button
        onClick={() => onChange(undefined)}
        className="text-xs text-neutral-500 hover:text-category-error underline"
      >
        Reset all to theme defaults
      </button>
    </div>
  );
}

// ─── Conditional visibility (Wave 2) ─────────────────────────────────────────

const RULE_LABELS: Record<RuleClause["op"], string> = {
  country_is: "Country is",
  country_is_not: "Country is not",
  country_in: "Country in list",
  cart_total_gt: "Cart total >",
  cart_total_lt: "Cart total <",
  cart_contains_sku: "Cart contains SKU",
  cart_contains_product_type: "Cart contains product type",
  cart_contains_tag: "Cart contains tag",
  customer_is: "Customer is",
  device_is: "Device is",
  has_vault_token: "Has saved card (CIT)",
  day_of_week: "Day of week is",
  time_window: "Time is within window",
};

const ALL_OPS: RuleClause["op"][] = [
  "country_is",
  "country_is_not",
  "country_in",
  "cart_total_gt",
  "cart_total_lt",
  "cart_contains_sku",
  "cart_contains_product_type",
  "cart_contains_tag",
  "customer_is",
  "device_is",
  "has_vault_token",
  "day_of_week",
  "time_window",
];

function defaultClauseFor(op: RuleClause["op"]): RuleClause {
  switch (op) {
    case "country_is":
    case "country_is_not":
      return { op, value: "US" };
    case "country_in":
      return { op, value: ["US", "CA"] };
    case "cart_total_gt":
      return { op, value: 10000 };
    case "cart_total_lt":
      return { op, value: 5000 };
    case "cart_contains_sku":
      return { op, value: "" };
    case "cart_contains_product_type":
      return { op, value: "" };
    case "cart_contains_tag":
      return { op, value: "" };
    case "customer_is":
      return { op, value: "new" };
    case "device_is":
      return { op, value: "mobile" };
    case "has_vault_token":
      return { op };
    case "day_of_week":
      return { op, value: [1, 2, 3, 4, 5] };
    case "time_window":
      return { op, value: { start_hhmm: "09:00", end_hhmm: "17:00" } };
  }
}

function ConditionalRulesRow({
  rules,
  onChange,
}: {
  rules: BlockRules;
  onChange: (r: BlockRules) => void;
}) {
  const group = rules.visibility_rules ?? {};
  const clauses: RuleClause[] = group.all ?? [];

  const setClauses = (next: RuleClause[]) => {
    const nextGroup = next.length === 0 ? undefined : { ...group, all: next };
    onChange({ ...rules, visibility_rules: nextGroup });
  };

  return (
    <div className="space-y-1.5 text-xs">
      <p className="text-[11px] text-neutral-500 -mt-1">
        Show when <span className="uppercase tracking-wide text-neutral-400">all match</span>.
      </p>
      {clauses.length === 0 ? (
        <p className="text-[11px] text-neutral-500 italic">Always visible. Add a condition to hide based on country, cart, customer, or time.</p>
      ) : (
        clauses.map((c, i) => (
          <ClauseRow
            key={i}
            clause={c}
            onChange={(next) => {
              const copy = clauses.slice();
              copy[i] = next;
              setClauses(copy);
            }}
            onRemove={() => setClauses(clauses.filter((_, j) => j !== i))}
          />
        ))
      )}
      <div className="flex gap-1 pt-1">
        <select
          defaultValue=""
          onChange={(e) => {
            const op = e.target.value as RuleClause["op"];
            if (!op) return;
            setClauses([...clauses, defaultClauseFor(op)]);
            e.target.value = "";
          }}
          className="flex-1 rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-1.5 py-1"
        >
          <option value="">+ Add condition…</option>
          {ALL_OPS.map((op) => (
            <option key={op} value={op}>{RULE_LABELS[op]}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

function ClauseRow({
  clause,
  onChange,
  onRemove,
}: {
  clause: RuleClause;
  onChange: (c: RuleClause) => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center gap-1 rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-1.5 py-1">
      <span className="text-[11px] text-neutral-500 whitespace-nowrap">{RULE_LABELS[clause.op]}</span>
      <div className="flex-1 min-w-0">
        <ClauseValueField clause={clause} onChange={onChange} />
      </div>
      <button
        onClick={onRemove}
        aria-label="Remove condition"
        className="text-neutral-500 hover:text-category-error"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

function ClauseValueField({
  clause,
  onChange,
}: {
  clause: RuleClause;
  onChange: (c: RuleClause) => void;
}) {
  switch (clause.op) {
    case "country_is":
    case "country_is_not":
    case "cart_contains_sku":
    case "cart_contains_product_type":
    case "cart_contains_tag":
      return (
        <input
          type="text"
          value={clause.value}
          onChange={(e) => onChange({ ...clause, value: e.target.value } as RuleClause)}
          placeholder={clause.op.startsWith("country") ? "US" : "value"}
          className="w-full rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-1.5 py-0.5 text-xs"
        />
      );
    case "country_in":
      return (
        <input
          type="text"
          value={clause.value.join(", ")}
          onChange={(e) =>
            onChange({ ...clause, value: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })
          }
          placeholder="US, CA, GB"
          className="w-full rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-1.5 py-0.5 text-xs"
        />
      );
    case "cart_total_gt":
    case "cart_total_lt":
      return (
        <div className="flex items-center gap-1">
          <input
            type="number"
            value={clause.value / 100}
            onChange={(e) =>
              onChange({ ...clause, value: Math.round(Number.parseFloat(e.target.value || "0") * 100) })
            }
            className="w-20 rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-1.5 py-0.5 text-xs"
          />
          <span className="text-[11px] text-neutral-500">(major units)</span>
        </div>
      );
    case "customer_is":
      return (
        <select
          value={clause.value}
          onChange={(e) => onChange({ ...clause, value: e.target.value as "new" | "returning" | "logged_in" })}
          className="w-full rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-1.5 py-0.5 text-xs"
        >
          <option value="new">new</option>
          <option value="returning">returning</option>
          <option value="logged_in">logged in</option>
        </select>
      );
    case "device_is":
      return (
        <select
          value={clause.value}
          onChange={(e) => onChange({ ...clause, value: e.target.value as Device })}
          className="w-full rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-1.5 py-0.5 text-xs"
        >
          <option value="desktop">desktop</option>
          <option value="tablet">tablet</option>
          <option value="mobile">mobile</option>
        </select>
      );
    case "has_vault_token":
      return <span className="text-[11px] text-neutral-500 italic">no value needed</span>;
    case "day_of_week": {
      const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
      return (
        <div className="flex gap-0.5 flex-wrap">
          {days.map((label, idx) => {
            const day = idx as 0 | 1 | 2 | 3 | 4 | 5 | 6;
            const active = clause.value.includes(day);
            return (
              <button
                key={label}
                type="button"
                onClick={() => {
                  const next = active
                    ? clause.value.filter((d) => d !== day)
                    : [...clause.value, day];
                  onChange({ ...clause, value: next as (typeof clause)["value"] });
                }}
                className={clsx(
                  "rounded px-1.5 py-0.5 text-[10px] border",
                  active
                    ? "border-neutral-900 dark:border-neutral-100 bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900"
                    : "border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300",
                )}
              >
                {label}
              </button>
            );
          })}
        </div>
      );
    }
    case "time_window":
      return (
        <div className="flex items-center gap-1">
          <input
            type="time"
            value={clause.value.start_hhmm}
            onChange={(e) => onChange({ ...clause, value: { ...clause.value, start_hhmm: e.target.value } })}
            className="rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-1.5 py-0.5 text-xs"
          />
          <span className="text-[11px] text-neutral-500">–</span>
          <input
            type="time"
            value={clause.value.end_hhmm}
            onChange={(e) => onChange({ ...clause, value: { ...clause.value, end_hhmm: e.target.value } })}
            className="rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-1.5 py-0.5 text-xs"
          />
        </div>
      );
  }
}

function VisibilityRow({
  rules,
  onChange,
}: {
  rules: BlockRules;
  onChange: (r: BlockRules) => void;
}) {
  const hideOn: Device[] = rules.hide_on_device ?? [];
  const toggle = (d: Device) => {
    const next = hideOn.includes(d) ? hideOn.filter((x) => x !== d) : [...hideOn, d];
    onChange({ ...rules, hide_on_device: next });
  };
  return (
    <div className="space-y-1.5 text-xs">
      <p className="text-[11px] text-neutral-500 -mt-1">Hide this block on certain device sizes.</p>
      <div className="flex gap-1">
        {(["desktop", "tablet", "mobile"] as Device[]).map((d) => {
          const active = hideOn.includes(d);
          return (
            <button
              key={d}
              type="button"
              onClick={() => toggle(d)}
              className={clsx(
                "flex-1 rounded border px-2 py-1 capitalize",
                active
                  ? "border-amber-400 bg-amber-50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-200"
                  : "border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-900",
              )}
              title={active ? `Hidden on ${d}` : `Shown on ${d}`}
            >
              {d}
            </button>
          );
        })}
      </div>
      {hideOn.length > 0 ? (
        <p className="text-[11px] text-neutral-500">
          Block is hidden on: {hideOn.join(", ")}
        </p>
      ) : (
        <p className="text-[11px] text-neutral-500">Visible on all devices.</p>
      )}
    </div>
  );
}

function BlockConfigFields({
  block,
  config,
  onChange,
}: {
  block: BlockId;
  config: LayoutBlockConfig;
  onChange: (cfg: LayoutBlockConfig) => void;
}) {
  // Custom HTML instances — merchant can have many per section. All resolve
  // to the same editor; `block` is the per-instance key so state routes to
  // the right html_id.
  if (isCustomHtmlId(block)) {
    return <CustomHtmlEditor block={block} config={config} onChange={onChange} />;
  }
  switch (block) {
    case "phone":
      return (
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={config.phone?.required ?? false}
            onChange={(e) =>
              onChange({ ...config, phone: { required: e.target.checked } })
            }
          />
          Phone is required
        </label>
      );
    case "company_field":
      return (
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={config.company_field?.required ?? false}
            onChange={(e) =>
              onChange({ ...config, company_field: { required: e.target.checked } })
            }
          />
          Company field is required
        </label>
      );
    case "address": {
      const cur = config.address ?? {};
      const patch = (p: Partial<NonNullable<LayoutBlockConfig["address"]>>) =>
        onChange({ ...config, address: { ...cur, ...p } });
      const Toggle = ({
        label,
        checked,
        onChange: onC,
      }: {
        label: string;
        checked: boolean;
        onChange: (v: boolean) => void;
      }) => (
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={checked} onChange={(e) => onC(e.target.checked)} />
          {label}
        </label>
      );
      return (
        <div className="space-y-2">
          <p className="text-[11px] text-neutral-500">
            Turn on the extra fields you need. Company / VAT / PO number render
            inline below the address; billing address becomes a second form
            when the shopper unchecks "same as shipping".
          </p>
          <Toggle
            label="Show company name"
            checked={!!cur.show_company}
            onChange={(v) => patch({ show_company: v })}
          />
          {cur.show_company ? (
            <label className="flex items-center gap-2 pl-6">
              <input
                type="checkbox"
                checked={!!cur.company_required}
                onChange={(e) => patch({ company_required: e.target.checked })}
              />
              <span className="text-[11px]">Required</span>
            </label>
          ) : null}

          <Toggle
            label="Show VAT ID (EU)"
            checked={!!cur.show_vat_id}
            onChange={(v) => patch({ show_vat_id: v })}
          />
          {cur.show_vat_id ? (
            <label className="flex items-center gap-2 pl-6">
              <input
                type="checkbox"
                checked={!!cur.vat_id_validate}
                onChange={(e) => patch({ vat_id_validate: e.target.checked })}
              />
              <span className="text-[11px]">Validate via VIES (coming soon)</span>
            </label>
          ) : null}

          <Toggle
            label="Show PO number"
            checked={!!cur.show_po_number}
            onChange={(v) => patch({ show_po_number: v })}
          />
          {cur.show_po_number ? (
            <label className="flex items-center gap-2 pl-6">
              <input
                type="checkbox"
                checked={!!cur.po_number_required}
                onChange={(e) => patch({ po_number_required: e.target.checked })}
              />
              <span className="text-[11px]">Required</span>
            </label>
          ) : null}

          <Toggle
            label='Show "Billing same as shipping" checkbox'
            checked={!!cur.show_billing_same_as_shipping}
            onChange={(v) => patch({ show_billing_same_as_shipping: v })}
          />
          {cur.show_billing_same_as_shipping ? (
            <label className="flex items-center gap-2 pl-6">
              <input
                type="checkbox"
                checked={cur.billing_default_same !== false}
                onChange={(e) => patch({ billing_default_same: e.target.checked })}
              />
              <span className="text-[11px]">Checked by default</span>
            </label>
          ) : null}
        </div>
      );
    }
    case "marketing_consent":
      return (
        <>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={config.marketing_consent?.default_opt_in ?? false}
              onChange={(e) =>
                onChange({
                  ...config,
                  marketing_consent: {
                    copy: config.marketing_consent?.copy ?? "Email me about offers",
                    ...(config.marketing_consent ?? {}),
                    default_opt_in: e.target.checked,
                  },
                })
              }
            />
            Default opt-in (unchecked recommended for GDPR)
          </label>
          <TextArea
            label="Copy"
            rows={2}
            value={config.marketing_consent?.copy ?? "Email me about offers"}
            onChange={(v) =>
              onChange({
                ...config,
                marketing_consent: {
                  default_opt_in: config.marketing_consent?.default_opt_in ?? false,
                  ...(config.marketing_consent ?? {}),
                  copy: v,
                },
              })
            }
          />
        </>
      );
    case "promo_code":
      return (
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={config.promo_code?.auto_apply_on_entry ?? false}
            onChange={(e) =>
              onChange({
                ...config,
                promo_code: { auto_apply_on_entry: e.target.checked },
              })
            }
          />
          Auto-apply on entry
        </label>
      );
    case "trust_badges": {
      const cur = config.trust_badges ?? {};
      const mode = cur.mode ?? "payment_icons";
      const alignment = cur.alignment ?? "center";
      const icons = cur.icon_ids ?? ["visa", "mastercard", "amex", "mobilepay", "vipps"];
      const badges = cur.badge_ids ?? [];
      const PAYMENT_BRAND_IDS = [
        "visa", "mastercard", "maestro", "electron", "amex",
        "dankort", "mobilepay", "vipps", "vipps_mobilepay", "swish",
        "apple_pay", "google_pay",
      ];
      return (
        <div className="space-y-3">
          <div>
            <p className="font-medium mb-1">Mode</p>
            <div className="flex rounded border border-neutral-200 dark:border-neutral-700 overflow-hidden">
              {(["payment_icons", "text"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => onChange({ ...config, trust_badges: { ...cur, mode: m } })}
                  className={clsx(
                    "flex-1 px-2 py-1 text-[11px] capitalize",
                    mode === m
                      ? "bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900"
                      : "text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800",
                  )}
                >
                  {m.replace("_", " ")}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="font-medium mb-1">Alignment</p>
            <div className="flex rounded border border-neutral-200 dark:border-neutral-700 overflow-hidden">
              {(["left", "center", "right"] as const).map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => onChange({ ...config, trust_badges: { ...cur, alignment: a } })}
                  className={clsx(
                    "flex-1 px-2 py-1 text-[11px] capitalize",
                    alignment === a
                      ? "bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900"
                      : "text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800",
                  )}
                >
                  {a}
                </button>
              ))}
            </div>
          </div>
          {mode === "payment_icons" ? (
            <div>
              <p className="font-medium mb-1">Payment icons</p>
              <div className="grid grid-cols-2 gap-1">
                {PAYMENT_BRAND_IDS.map((b) => {
                  const on = icons.includes(b);
                  return (
                    <label
                      key={b}
                      className={clsx(
                        "flex items-center gap-2 rounded px-1.5 py-1 border",
                        on
                          ? "border-neutral-400 dark:border-neutral-500 bg-white dark:bg-neutral-800"
                          : "border-neutral-200 dark:border-neutral-700",
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...icons, b]
                            : icons.filter((x) => x !== b);
                          onChange({ ...config, trust_badges: { ...cur, icon_ids: next } });
                        }}
                      />
                      <span
                        className="inline-block bg-white rounded border border-neutral-200"
                        style={{ width: 38, height: 24, padding: 0, lineHeight: 0 }}
                        dangerouslySetInnerHTML={{ __html: BRAND_SVG[b] ?? "" }}
                      />
                      <span className="text-[10px] text-neutral-500 truncate">{b}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ) : (
            <div>
              <p className="font-medium mb-1">Badge IDs</p>
              {["ssl", "pci", "money_back_30d", "norton", "trustpilot_5"].map((b) => (
                <label key={b} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={badges.includes(b)}
                    onChange={(e) => {
                      const next = e.target.checked
                        ? [...badges, b]
                        : badges.filter((x) => x !== b);
                      onChange({ ...config, trust_badges: { ...cur, badge_ids: next } });
                    }}
                  />
                  {b}
                </label>
              ))}
            </div>
          )}
        </div>
      );
    }
    case "gift_message":
      return (
        <>
          <NumberField
            label="Max length"
            value={config.gift_message?.max_length ?? 250}
            onChange={(n) =>
              onChange({
                ...config,
                gift_message: {
                  attach_as: config.gift_message?.attach_as ?? "line_item_property",
                  max_length: n,
                },
              })
            }
          />
          <div>
            <p className="font-medium mb-1">Attach as</p>
            <select
              value={config.gift_message?.attach_as ?? "line_item_property"}
              onChange={(e) =>
                onChange({
                  ...config,
                  gift_message: {
                    max_length: config.gift_message?.max_length ?? 250,
                    attach_as: e.target.value as "line_item_property" | "order_note",
                  },
                })
              }
              className="w-full rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-1.5 py-1"
            >
              <option value="line_item_property">Line item property</option>
              <option value="order_note">Order note</option>
            </select>
          </div>
        </>
      );
    case "shipping_insurance":
      return (
        <>
          <p className="text-[11px] text-neutral-500 italic">
            Underwriter: cost_plus. Legal sign-off pending — this block is
            "Coming soon" in production; in Studio you can preview.
          </p>
          <NumberField
            label="Fee (cents)"
            value={config.shipping_insurance?.fee_cents ?? 299}
            onChange={(n) =>
              onChange({
                ...config,
                shipping_insurance: {
                  underwriter: "cost_plus",
                  currency: config.shipping_insurance?.currency ?? "EUR",
                  fee_cents: n,
                },
              })
            }
          />
          <TextField
            label="Currency (ISO)"
            value={config.shipping_insurance?.currency ?? "EUR"}
            onChange={(v) =>
              onChange({
                ...config,
                shipping_insurance: {
                  underwriter: "cost_plus",
                  fee_cents: config.shipping_insurance?.fee_cents ?? 299,
                  currency: v,
                },
              })
            }
          />
        </>
      );
    case "text_banner":
      return (
        <TextArea
          label="Banner copy"
          rows={3}
          value={config.text_banner?.copy ?? ""}
          onChange={(v) =>
            onChange({ ...config, text_banner: { copy: v } })
          }
        />
      );
    case "image_banner":
      return <ImageBannerUploader config={config} onChange={onChange} />;
    case "billing_same_as_shipping":
      return (
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={config.billing_same_as_shipping?.default_checked ?? true}
            onChange={(e) =>
              onChange({
                ...config,
                billing_same_as_shipping: { default_checked: e.target.checked },
              })
            }
          />
          Checked by default
        </label>
      );
    // ─── Wave 3 block configs ─────────────────────────────────────────────
    case "newsletter_opt_in": {
      const cur = config.newsletter_opt_in ?? { default_opt_in: false, copy: "Sign me up for the newsletter — I can unsubscribe any time." };
      return (
        <>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={cur.default_opt_in}
              onChange={(e) => onChange({ ...config, newsletter_opt_in: { ...cur, default_opt_in: e.target.checked } })}
            />
            Default opt-in (unchecked recommended for GDPR)
          </label>
          <TextArea label="Copy" rows={2} value={cur.copy}
            onChange={(v) => onChange({ ...config, newsletter_opt_in: { ...cur, copy: v } })} />
        </>
      );
    }
    case "sms_opt_in": {
      const cur = config.sms_opt_in ?? { default_opt_in: false, copy: "Text me order updates and offers. Msg & data rates may apply. Reply STOP to opt out." };
      return (
        <>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={cur.default_opt_in}
              onChange={(e) => onChange({ ...config, sms_opt_in: { ...cur, default_opt_in: e.target.checked } })}
            />
            Default opt-in (leave off for TCPA/GDPR)
          </label>
          <TextArea label="Copy" rows={3} value={cur.copy}
            onChange={(v) => onChange({ ...config, sms_opt_in: { ...cur, copy: v } })} />
        </>
      );
    }
    case "login_link": {
      const cur = config.login_link ?? { copy: "Already have an account? Sign in", href: "/signin" };
      return (
        <>
          <TextField label="Link copy" value={cur.copy}
            onChange={(v) => onChange({ ...config, login_link: { ...cur, copy: v } })} />
          <TextField label="Sign-in URL" value={cur.href}
            onChange={(v) => onChange({ ...config, login_link: { ...cur, href: v } })} />
        </>
      );
    }
    case "loyalty_login_card": {
      const cur = config.loyalty_login_card ?? {
        title: "Have rewards or discounts?",
        link_label: "Sign in to apply them",
        href: "/signin",
      };
      return (
        <>
          <TextField label="Title" value={cur.title}
            onChange={(v) => onChange({ ...config, loyalty_login_card: { ...cur, title: v } })} />
          <TextField label="Link label" value={cur.link_label}
            onChange={(v) => onChange({ ...config, loyalty_login_card: { ...cur, link_label: v } })} />
          <TextField label="Sign-in URL" value={cur.href}
            onChange={(v) => onChange({ ...config, loyalty_login_card: { ...cur, href: v } })} />
        </>
      );
    }
    case "save_my_info": {
      const cur = config.save_my_info ?? { default_checked: true, copy: "Save my info for a faster checkout next time" };
      return (
        <>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={cur.default_checked}
              onChange={(e) => onChange({ ...config, save_my_info: { ...cur, default_checked: e.target.checked } })}
            />
            Checked by default
          </label>
          <TextArea label="Copy" rows={2} value={cur.copy}
            onChange={(v) => onChange({ ...config, save_my_info: { ...cur, copy: v } })} />
        </>
      );
    }
    case "vat_id_field": {
      const cur = config.vat_id_field ?? { required: false, validate: false };
      return (
        <>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={cur.required}
              onChange={(e) => onChange({ ...config, vat_id_field: { ...cur, required: e.target.checked } })} />
            Required
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={cur.validate}
              onChange={(e) => onChange({ ...config, vat_id_field: { ...cur, validate: e.target.checked } })} />
            Validate via VIES (EU) — coming soon
          </label>
        </>
      );
    }
    case "po_number": {
      const cur = config.po_number ?? { required: false };
      return (
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={cur.required}
            onChange={(e) => onChange({ ...config, po_number: { required: e.target.checked } })} />
          Required
        </label>
      );
    }
    case "delivery_date": {
      const cur = config.delivery_date ?? { min_days_out: 1, max_days_out: 14 };
      return (
        <>
          <NumberField label="Earliest (days out)" value={cur.min_days_out}
            onChange={(n) => onChange({ ...config, delivery_date: { ...cur, min_days_out: n } })} />
          <NumberField label="Latest (days out)" value={cur.max_days_out}
            onChange={(n) => onChange({ ...config, delivery_date: { ...cur, max_days_out: n } })} />
        </>
      );
    }
    case "save_card_toggle": {
      const cur = config.save_card_toggle ?? { default_checked: true, copy: "Save this card for faster checkout next time" };
      return (
        <>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={cur.default_checked}
              onChange={(e) => onChange({ ...config, save_card_toggle: { ...cur, default_checked: e.target.checked } })} />
            Checked by default
          </label>
          <TextArea label="Copy" rows={2} value={cur.copy}
            onChange={(v) => onChange({ ...config, save_card_toggle: { ...cur, copy: v } })} />
        </>
      );
    }
    case "terms_checkbox": {
      const cur = config.terms_checkbox ?? {
        required: true,
        copy: "I agree to the",
        terms_url: "/terms",
        link_title: "Terms & Conditions",
      };
      return (
        <>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={cur.required}
              onChange={(e) => onChange({ ...config, terms_checkbox: { ...cur, required: e.target.checked } })} />
            Required (recommended)
          </label>
          <TextArea label="Copy (text before the link)" rows={2} value={cur.copy}
            onChange={(v) => onChange({ ...config, terms_checkbox: { ...cur, copy: v } })} />
          <TextField label="Link title" value={cur.link_title ?? "Terms & Conditions"}
            onChange={(v) => onChange({ ...config, terms_checkbox: { ...cur, link_title: v } })} />
          <TextField label="Terms URL" value={cur.terms_url}
            onChange={(v) => onChange({ ...config, terms_checkbox: { ...cur, terms_url: v } })} />
        </>
      );
    }
    case "payment_method_selector": {
      const cur = config.payment_method_selector ?? {};
      const showIcons = cur.show_brand_icons !== false;
      const maxVisible = cur.max_visible_icons ?? 3;
      return (
        <>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={showIcons}
              onChange={(e) =>
                onChange({
                  ...config,
                  payment_method_selector: { ...cur, show_brand_icons: e.target.checked },
                })
              }
            />
            Show brand icons next to each method
          </label>
          {showIcons ? (
            <NumberField
              label="Max icons before '+N others'"
              value={maxVisible}
              onChange={(n) =>
                onChange({
                  ...config,
                  payment_method_selector: { ...cur, max_visible_icons: Math.max(0, n) },
                })
              }
            />
          ) : null}
          <p className="text-[11px] text-neutral-500 italic">
            Icons come from the method's default list (Visa/MC/Amex for Credit card, etc.).
            Buyer page hides brands the buyer's browser or country can't use. Overflow collapses
            into a transparent "+N others" pill.
          </p>
        </>
      );
    }
    case "age_verification": {
      const cur = config.age_verification ?? { minimum_age: 18 };
      return (
        <NumberField label="Minimum age" value={cur.minimum_age}
          onChange={(n) => onChange({ ...config, age_verification: { minimum_age: n } })} />
      );
    }
    case "subscription_consent": {
      const cur = config.subscription_consent ?? { copy: "Your cart includes a subscription. By paying, you authorize recurring charges at the stated interval until you cancel." };
      return (
        <>
          <p className="text-[11px] text-neutral-500 italic">
            This disclosure is system-generated for subscription carts. Edit the copy but keep
            the legal intent — MIT (merchant-initiated transaction) consent is required.
          </p>
          <TextArea label="Copy" rows={4} value={cur.copy}
            onChange={(v) => onChange({ ...config, subscription_consent: { copy: v } })} />
        </>
      );
    }
    case "tax_breakdown": {
      const cur = config.tax_breakdown ?? { show_if_no_tax: false };
      return (
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={cur.show_if_no_tax}
            onChange={(e) => onChange({ ...config, tax_breakdown: { show_if_no_tax: e.target.checked } })} />
          Show even when no tax applies
        </label>
      );
    }
    case "gift_card_input": {
      const cur = config.gift_card_input ?? { label: "Gift card or store credit" };
      return (
        <TextField label="Placeholder" value={cur.label}
          onChange={(v) => onChange({ ...config, gift_card_input: { label: v } })} />
      );
    }
    case "ic_fee_transparency": {
      const cur = config.ic_fee_transparency ?? { show_interchange: true, show_scheme: true, show_cost_plus_fee: true };
      return (
        <>
          <p className="text-[11px] text-neutral-500 italic">
            IC++ line-item breakdown pending — the orchestrator will surface per-order
            interchange + scheme data once Cost+ plumbing lands. Ship the block now with
            these toggles; real data will light up the display.
          </p>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={cur.show_interchange}
              onChange={(e) => onChange({ ...config, ic_fee_transparency: { ...cur, show_interchange: e.target.checked } })} />
            Show interchange fee
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={cur.show_scheme}
              onChange={(e) => onChange({ ...config, ic_fee_transparency: { ...cur, show_scheme: e.target.checked } })} />
            Show scheme fee
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={cur.show_cost_plus_fee}
              onChange={(e) => onChange({ ...config, ic_fee_transparency: { ...cur, show_cost_plus_fee: e.target.checked } })} />
            Show Cost+ fee
          </label>
        </>
      );
    }
    case "product_thumbnails": {
      const cur = config.product_thumbnails ?? { size_px: 48 };
      return (
        <div>
          <p className="font-medium mb-1">Thumbnail size</p>
          <select
            value={cur.size_px}
            onChange={(e) => onChange({ ...config, product_thumbnails: { size_px: Number.parseInt(e.target.value, 10) } })}
            className="w-full rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-1.5 py-1"
          >
            <option value={32}>Small (32px)</option>
            <option value={48}>Medium (48px)</option>
            <option value={64}>Large (64px)</option>
            <option value={96}>XL (96px)</option>
          </select>
        </div>
      );
    }
    case "recommendation_cards": {
      const cur = config.recommendation_cards ?? { title: "Most customers also like" };
      return (
        <>
          <TextField label="Heading" value={cur.title}
            onChange={(v) => onChange({ ...config, recommendation_cards: { ...cur, title: v } })} />
          <p className="text-[11px] text-neutral-500 italic">
            Product-feed backed recommendations are a later provider hook. The preview uses cart items until real items are configured.
          </p>
        </>
      );
    }
    case "benefit_rows": {
      return (
        <p className="text-[11px] text-neutral-500 italic">
          Uses the default three service-benefit rows for now. Per-row editing can land with the next content-editor pass.
        </p>
      );
    }
    case "testimonial_cards": {
      const cur = config.testimonial_cards ?? { title: "What customers say" };
      return (
        <>
          <TextField label="Heading (optional)" value={cur.title ?? ""}
            onChange={(v) => onChange({ ...config, testimonial_cards: { ...cur, title: v || undefined } })} />
          <p className="text-[11px] text-neutral-500 italic">
            Review provider import is not connected yet; the block renders safe default cards until reviews are configured.
          </p>
        </>
      );
    }
    default:
      return (
        <p className="text-neutral-500 italic">
          This block has no configuration in v1.
        </p>
      );
  }
}

type CustomHtmlBlockKey = `custom_html_${string}`;

function CustomHtmlEditor({
  block,
  config,
  onChange,
}: {
  block: BlockId;
  config: LayoutBlockConfig;
  onChange: (cfg: LayoutBlockConfig) => void;
}) {
  const key = block as CustomHtmlBlockKey;
  const existing = config[key];
  const [rawHtml, setRawHtml] = useState(() => (existing ? loadCustomHtml(existing.html_id) : ""));
  const [height, setHeight] = useState(existing?.sandbox_height_px ?? 60);
  const sanitized = useMemo(() => {
    if (!existing) return "";
    return rawHtml;
  }, [rawHtml, existing]);

  function saveHtml(next: string) {
    setRawHtml(next);
    const id = existing?.html_id ?? `html_${crypto.getRandomValues(new Uint8Array(4)).reduce((s, b) => s + b.toString(16).padStart(2, "0"), "")}`;
    saveCustomHtml(id, next);
    onChange({
      ...config,
      [key]: { html_id: id, sandbox_height_px: height },
    });
  }

  function updateHeight(n: number) {
    setHeight(n);
    if (existing) {
      onChange({
        ...config,
        [key]: { html_id: existing.html_id, sandbox_height_px: n },
      });
    }
  }

  function removeHtml() {
    if (existing) deleteCustomHtml(existing.html_id);
    setRawHtml("");
    const { [key]: _removed, ...rest } = config;
    onChange(rest);
  }

  return (
    <>
      <p className="text-[11px] text-neutral-500">
        HTML renders inside a sandboxed iframe with{" "}
        <code>sandbox="allow-same-origin"</code> and a strict CSP. Scripts,
        forms, and top-navigation are blocked (FLOWS.md § 11.9 C3).
      </p>
      <textarea
        rows={8}
        spellCheck={false}
        value={rawHtml}
        onChange={(e) => saveHtml(e.target.value)}
        placeholder="<p>Free returns within 30 days.</p>"
        className="w-full rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-2 py-1.5 text-xs font-mono"
      />
      <div>
        <p className="font-medium mb-1">Iframe height</p>
        <select
          value={height}
          onChange={(e) => updateHeight(Number.parseInt(e.target.value, 10))}
          className="w-full rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-1.5 py-1"
        >
          {[40, 60, 80, 120, 200].map((h) => (
            <option key={h} value={h}>
              {h} px
            </option>
          ))}
        </select>
      </div>
      {sanitized ? (
        <div>
          <p className="text-[11px] text-neutral-500 mb-1">Sandbox preview:</p>
          <iframe
            srcDoc={sanitized}
            sandbox="allow-same-origin"
            className="w-full border border-neutral-200 dark:border-neutral-700 rounded"
            style={{ height }}
          />
        </div>
      ) : null}
      {existing ? (
        <button
          onClick={removeHtml}
          className="text-xs text-category-error hover:underline"
        >
          Remove HTML
        </button>
      ) : null}
    </>
  );
}

function LogoUploader({
  current,
  onChange,
}: {
  current?: string;
  onChange: (url: string | undefined) => void;
}) {
  async function onUpload(file: File) {
    try {
      const { getApiClient } = await import("../../lib/apiClient");
      const meta = await getApiClient().media.uploadBanner(file, { alt: "Logo" });
      // In Studio mode media.cdn_url is a data URL; in Connected mode
      // it's a real CDN URL. Either way the buyer page can load it.
      onChange(meta.cdn_url);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Upload failed");
    }
  }
  return (
    <div className="space-y-1.5">
      <label className="text-xs text-neutral-700 dark:text-neutral-300 block">
        Checkout logo
      </label>
      {current ? (
        <div className="flex items-center gap-2">
          <img
            src={current}
            alt="Logo"
            style={{ maxHeight: 40, maxWidth: 160, objectFit: "contain" }}
            className="bg-white border border-neutral-200 dark:border-neutral-700 rounded p-1"
          />
          <button
            type="button"
            onClick={() => onChange(undefined)}
            className="text-[11px] text-neutral-500 hover:text-category-error underline"
          >
            Remove
          </button>
        </div>
      ) : (
        <p className="text-[11px] text-neutral-500">
          No logo — the top bar shows "Checkout" text.
        </p>
      )}
      <input
        type="file"
        accept="image/jpeg,image/png,image/webp,image/svg+xml"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void onUpload(file);
          e.target.value = "";
        }}
        className="text-xs"
      />
    </div>
  );
}

function ImageBannerUploader({
  config,
  onChange,
}: {
  config: LayoutBlockConfig;
  onChange: (cfg: LayoutBlockConfig) => void;
}) {
  const existing = config.image_banner;
  const [alt, setAlt] = useState(existing?.alt ?? "");

  async function onUpload(file: File) {
    try {
      const { getApiClient } = await import("../../lib/apiClient");
      const meta = await getApiClient().media.uploadBanner(file, { alt });
      onChange({
        ...config,
        image_banner: {
          image_id: meta.cdn_url, // In Studio mode cdn_url is the data URL
          alt,
          link_url: existing?.link_url ?? null,
        },
      });
    } catch (err) {
      alert(err instanceof Error ? err.message : "Upload failed");
    }
  }

  return (
    <>
      <TextField
        label="Alt text"
        value={alt}
        onChange={(v) => {
          setAlt(v);
          if (existing) {
            onChange({
              ...config,
              image_banner: { ...existing, alt: v },
            });
          }
        }}
      />
      <input
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void onUpload(file);
        }}
        className="text-xs"
      />
      {existing?.image_id ? (
        <div>
          <p className="text-[11px] text-neutral-500 mb-1">Preview:</p>
          <img
            src={existing.image_id}
            alt={existing.alt}
            className="w-full rounded border border-neutral-200 dark:border-neutral-700"
          />
        </div>
      ) : null}
    </>
  );
}

// ─── Small primitives ────────────────────────────────────────────────────────

function TextField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <p className="font-medium mb-1">{label}</p>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-1.5 py-1"
      />
    </div>
  );
}

function NumberField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
}) {
  return (
    <div>
      <p className="font-medium mb-1">{label}</p>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(Number.parseInt(e.target.value, 10) || 0)}
        className="w-full rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-1.5 py-1"
      />
    </div>
  );
}

function TextArea({
  label,
  rows,
  value,
  onChange,
}: {
  label: string;
  rows: number;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <p className="font-medium mb-1">{label}</p>
      <textarea
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-1.5 py-1"
      />
    </div>
  );
}

// triggerShopifyShopDomain returns the shop reference of the flow's
// trigger node when bound to Shopify. Used to render the Shopify cart
// icon in the topbar preview when the flow's source is Shopify.
function triggerShopifyShopDomain(definition: FlowDefinition | null): string | null {
  if (!definition) return null;
  const start = definition.nodes[definition.start_node];
  if (!start || start.type !== "trigger") return null;
  const shopify = start.sources.find((s) => s.type === "shopify" && s.reference);
  return shopify?.reference ?? null;
}
